import { Typography } from '@mui/material';
import type { Condition } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { PanelWrapper } from '@/components/panels/panelWrapper';
import {
  sourceAccountId,
  cloudProvider,
  resourceGroupName,
  resourceId,
  resourceName,
  resourceType,
  sourceRegion,
  subnets,
  vpc,
  tagKeys,
  tagKeyValues,
} from '@/components/queryBuilder/properties';
import type { ResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { ResourcesSelectionEditor } from '@/components/resourcesSelection/resourcesSelectionEditor';
import { useDAL } from '@/contexts/useDAL';
import {
  createAllExcludedCondition,
  createAllIncludedCondition,
  type FilterProperty,
} from '@/types/advanceFilter';

import {
  backupScopesResourcesAllExcludedDescription,
  backupScopesResourcesAllIncludedDescription,
} from '../backupScopesResources';

export const backupScopeSupportedProperties: FilterProperty[][] = [
  [resourceType],
  [
    cloudProvider,
    sourceAccountId,
    sourceRegion,
    vpc,
    subnets,
    resourceGroupName,
  ],
  [resourceName, resourceId],
  [tagKeys, tagKeyValues],
];

export const BackupScopeResourcesPanel = (props: {
  condition?: Condition;
  resourcesScopeType: ResourcesScopeType;
  include: string[];
  exclude: string[];
  onClose: () => void;
  onScopeChange: () => Promise<void>;
}) => {
  const dal = useDAL();
  const [isSaving, setIsSaving] = useState(false);
  const [condition, setCondition] = useState(props.condition);
  const [include, setInclude] = useState(props.include);
  const [exclude, setExclude] = useState(props.exclude);
  const [resourcesScopeType, setResourcesScopeType] =
    useState<ResourcesScopeType>(props.resourcesScopeType);

  const onSave = async () => {
    setIsSaving(true);
    try {
      const conditionValue =
        resourcesScopeType === 'allIncluded'
          ? createAllIncludedCondition()
          : resourcesScopeType === 'allExcluded'
            ? createAllExcludedCondition()
            : condition;

      if (conditionValue) {
        await dal.inventory.backupScope.save({
          condition: conditionValue,
          include,
          exclude,
        });
        await props.onScopeChange();
        props.onClose();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <PanelWrapper
        header={{
          title: 'Edit resource scope',
          subtitle: (
            <Typography variant='body1'>
              Choose which resources are included in your discovery process.
              <br />
              Included resources are discovered, scanned, classified, and backed
              up if matched by a policy.
            </Typography>
          ),
          onClose: props.onClose,
        }}
        footer={{
          submitInProgress: isSaving,
          primaryButton: {
            'data-testid': 'save-resource-scope-button',
            text: 'Save',
            onClick: onSave,
          },
          secondaryButton: {
            'data-testid': 'cancel-resource-scope-button',
            text: 'Cancel',
            onClick: props.onClose,
          },
        }}
      >
        <ResourcesSelectionEditor
          condition={condition}
          includedResources={include}
          excludedResources={exclude}
          setIncludedResources={(includedResources) =>
            setInclude(includedResources)
          }
          setExcludedResources={(excludedResources) =>
            setExclude(excludedResources)
          }
          resourcesScopeType={resourcesScopeType}
          setResourcesScopeType={(resourcesScopeType) =>
            setResourcesScopeType(resourcesScopeType)
          }
          setCondition={(condition) => setCondition(condition)}
          supportedProperties={backupScopeSupportedProperties}
          conditionTitle='Discover resources based on these conditions:'
          allExcludedDescription={backupScopesResourcesAllExcludedDescription}
          allIncludedDescription={backupScopesResourcesAllIncludedDescription}
          manualOverridesDescription='Manually include or exclude resources from discovery process, regardless of dynamic selection.'
        />
      </PanelWrapper>
    </>
  );
};
